import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { BadgeCheck, UserPlus, Check, ArrowRight } from "lucide-react";
import api, { slugifyName } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuroraAvatar } from "@/components/AuthorAvatar";
import { CATEGORY_ROUTES } from "@/lib/types";

const ROLE_LABELS: Record<string, string> = {
  author: "Contributor · NewsLayer",
  editorial: "NewsLayer Editorial",
  deep_dive: "NewsLayer Research",
  market_brief: "NewsLayer Desk",
};

const catName = (slug: string) =>
  CATEGORY_ROUTES.find((c) => c.slug === slug)?.name ||
  slug.replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

/* Author end card (§37): editorial credibility, no metadata duplication. */
export const AuthorEndCard = ({ authorName, contentType }: {
  authorName?: string | null; contentType?: string | null;
}) => {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const slug = slugifyName(authorName || "");
  const isExternal = ["imported", "pr"].includes(contentType || "");

  const { data } = useQuery({
    queryKey: ["byline-author", slug],
    queryFn: async () => (await api.get(`/authors/${slug}`)).data,
    enabled: !!slug && !isExternal,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (!slug || isExternal) return null;
  const a = data?.author;
  const profileSlug = a?.handle || slug;
  const following = !!user?.followed_authors?.includes(a?.handle || slug) ||
    !!user?.followed_authors?.includes(slug);
  const beat: string[] = (a?.top_categories || []).slice(0, 3);
  const isSelf = !!user?.full_name && user.full_name === authorName;

  const follow = async () => {
    if (!user) { navigate("/login"); return; }
    setBusy(true);
    try {
      await api.post("/account/follow", { follow_type: "author", slug: profileSlug });
      await refresh();
      toast.success(`Following ${authorName} — you'll be alerted the moment they publish`);
    } catch { toast.error("Could not follow — try again"); }
    setBusy(false);
  };

  return (
    <section data-testid="author-end-card"
      className="mt-8 rounded-xl border border-white/[0.08] bg-[#0a0b10] p-5 sm:p-6">
      <p className="font-mono text-[9px] font-bold uppercase tracking-[0.28em] text-gray-500">About the Author</p>
      <div className="mt-4 flex flex-col sm:flex-row sm:items-start gap-4">
        <AuroraAvatar name={authorName!} avatarUrl={a?.avatar_url} className="h-16 w-16" textClass="text-lg" animate={false} />
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 font-news font-bold text-white text-[17px] leading-tight">
            {authorName}
            {a?.is_program_author && <BadgeCheck className="h-4 w-4 text-[#00E5FF] shrink-0" aria-label="Verified NewsLayer author" />}
          </p>
          <p className="font-mono text-[10.5px] text-gray-500 mt-0.5">
            {ROLE_LABELS[contentType || ""] || "NewsLayer"}
          </p>
          <p className="mt-2.5 text-[13.5px] text-gray-400 leading-relaxed line-clamp-3" data-testid="author-end-bio">
            {a?.bio || (beat.length ? `Covers ${beat.map(catName).join(", ")} for NewsLayer.` : "Writes for NewsLayer.")}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {!isSelf && (
              following ? (
                <span data-testid="author-end-following"
                  className="inline-flex items-center gap-1.5 rounded-md border border-[#34D399]/35 bg-[#34D399]/[0.08] px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-wider text-[#34D399]">
                  <Check className="h-3.5 w-3.5" /> Following
                </span>
              ) : (
                <button type="button" onClick={follow} disabled={busy} data-testid="author-end-follow"
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#7C3AED] hover:bg-[#8B5CF6] px-3 py-1.5 text-[12.5px] font-bold text-white transition-colors disabled:opacity-60">
                  <UserPlus className="h-3.5 w-3.5" /> Follow author
                </button>
              )
            )}
            <Link to={`/author/${profileSlug}`} data-testid="author-end-profile-link"
              className="inline-flex items-center gap-1.5 font-mono text-[10.5px] font-bold uppercase tracking-widest text-[#00E5FF] hover:text-white transition-colors">
              View profile <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
